import { useRef, useCallback } from 'react';
import { COMPONENT_VIEWED_EVENT_NAME } from './constants';
import { createElementViewedObserver, trackViewedEvent } from './viewed-tracking';
export function useTrackComponentViewed(componentNameOrConfig, propsOrDeps) {
    // Determine which overload is being used and extract parameters
    const isObjectSyntax = typeof componentNameOrConfig === 'object';
    // Extract parameters based on which syntax is being used
    const componentName = isObjectSyntax ? componentNameOrConfig.componentName : componentNameOrConfig;
    const props = isObjectSyntax ? componentNameOrConfig.props : propsOrDeps;
    const options = isObjectSyntax ? {
        eventProperties: componentNameOrConfig.eventProperties,
        threshold: componentNameOrConfig.threshold,
        trackOnce: componentNameOrConfig.trackOnce,
        eventName: componentNameOrConfig.eventName
    } : undefined;
    const deps = isObjectSyntax && Array.isArray(propsOrDeps) ? propsOrDeps : [];
    // Create refs for all values
    const componentNameRef = useRef(componentName);
    const propsRef = useRef(props);
    const optionsRef = useRef(options);
    const observerCleanupRef = useRef(null);
    // if props change, ensure that when we track we use the latest version
    componentNameRef.current = componentName;
    propsRef.current = props;
    optionsRef.current = options;
    // Ref callback that sets up tracking when the ref is attached
    return useCallback((node)=>{
        // Clean up previous observer if it exists
        if (observerCleanupRef.current) {
            observerCleanupRef.current();
            observerCleanupRef.current = null;
        }
        // Nothing more to do if there's no node
        if (!node) {
            return;
        }
        // Extract options with defaults
        const { threshold = 0.1, trackOnce = true, eventName = COMPONENT_VIEWED_EVENT_NAME } = optionsRef.current || {};
        // Set up the callback function to handle when the element is viewed
        const handleViewed = ()=>{
            const currentEventProperties = optionsRef.current?.eventProperties || {};
            // Track the event
            trackViewedEvent(eventName, {
                component_name: componentNameRef.current,
                component_props: propsRef.current,
                ...currentEventProperties
            });
        };
        // Create the observer and store its cleanup function
        observerCleanupRef.current = createElementViewedObserver(node, handleViewed, {
            threshold,
            trackOnce
        });
    }, deps); // Use the dependency array (for object syntax) or empty array (for simple syntax)
}
